import { useQuery } from "@apollo/client";
import { Delete, Edit, Add, Book } from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import { ReactElement, useState } from "react";

import {
  DeleteApiKeyMutation,
  DeleteApiKeyMutationVariables,
  ListApiKeysQuery,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import FullPageCard from "~frontend/components/FullPageCard";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import Table, {
  RowActions,
  TableBox,
  TableColumns,
} from "~frontend/components/Table";
import { DELETE_API_KEY, LIST_API_KEYS } from "~frontend/graphql/apiKeys";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import useMutation from "~frontend/util/useMutation";

import { CreateApiKeyDialog } from "./CreateApiKeyDialog";
import { UpdateApiKeyDialog } from "./UpdateApiKeyDialog";
import { ApiKey } from "./types";

export function ApiKeys(): ReactElement {
  const isEnabled = useFeatureFlag("apiKeys");

  const [createApiKeyDialogOpen, setCreateApiKeyDialogOpen] = useState(false);

  const [updateApiKeyDialogOpenFor, setUpdateApiKeyDialogOpenFor] =
    useState<ApiKey | null>(null);

  useBreadcrumbs([
    {
      label: "API Keys",
      path: "/organization/api-keys",
    },
  ]);

  const { loading, error, data } = useQuery<ListApiKeysQuery>(LIST_API_KEYS);

  const [deleteApiKeyMutation] = useMutation<
    DeleteApiKeyMutation,
    DeleteApiKeyMutationVariables
  >(DELETE_API_KEY, {
    onErrorSnackbarMessage: "Error deleting API key",
    refetchQueries: [LIST_API_KEYS],
    awaitRefetchQueries: true,
  });

  const columns: TableColumns<ApiKey> = [
    {
      key: "name",
      renderValue: row => row.name,
      title: "Name",
    },
    {
      key: "id",
      renderValue: row => row.id,
      title: "ID",
    },
    {
      key: "createdAt",
      renderValue: row => (
        <>
          <Typography>{new Date(row.createdAt).toLocaleString()}</Typography>
          <Typography variant="body3">By {row.createdBy.fullName}</Typography>
        </>
      ),
      title: "Created At",
    },
  ];

  const rowActions: RowActions<ApiKey> = [
    {
      id: "edit",
      type: "button",
      icon: <Edit />,
      tooltipLabel: "Edit API key name and scopes",
      onClick: apiKey => {
        setUpdateApiKeyDialogOpenFor(apiKey);
      },
    },
    {
      id: "delete",
      type: "button",
      icon: <Delete />,
      tooltipLabel: "Delete API key",
      confirm: apiKey => ({
        title: `Delete API Key ${apiKey.name}`,
        content: `Are you sure you want to delete the API key ${apiKey.name} (ID ${apiKey.id})? The key will immediately stop working. This action cannot be undone.`,
      }),
      onClick: async apiKey => {
        await deleteApiKeyMutation({
          variables: { input: { apiKeyId: apiKey.id } },
        });
      },
    },
  ];

  if (!isEnabled) {
    return (
      <FullPageCard title="API Keys Disabled">
        API keys are not enabled for your organization.
      </FullPageCard>
    );
  }

  return (
    <PageContainer>
      <AsyncWrapper loading={loading} errors={error} data={data}>
        {({ listApiKeys, listApiKeyScopes }) => {
          return (
            <>
              {updateApiKeyDialogOpenFor ? (
                <UpdateApiKeyDialog
                  onClose={() => setUpdateApiKeyDialogOpenFor(null)}
                  apiKey={updateApiKeyDialogOpenFor}
                  scopes={listApiKeyScopes}
                />
              ) : null}

              <CreateApiKeyDialog
                onClose={() => setCreateApiKeyDialogOpen(false)}
                scopes={listApiKeyScopes}
                open={createApiKeyDialogOpen}
              />

              <FlexBox justifyContent="space-between" sx={{ marginBottom: 2 }}>
                <PageHeader>API Keys</PageHeader>
                <Box>
                  <Button
                    href="/organization/api-keys/documentation"
                    target="_blank"
                    sx={{ mr: 1 }}
                  >
                    <Book />
                    View API Documentation
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => setCreateApiKeyDialogOpen(true)}
                  >
                    <Add />
                    Create API Key
                  </Button>
                </Box>
              </FlexBox>
              <TableBox>
                <Table
                  rows={listApiKeys}
                  columns={columns}
                  noFooter
                  rowActions={rowActions}
                />
              </TableBox>
            </>
          );
        }}
      </AsyncWrapper>
    </PageContainer>
  );
}
